/*
 * Copyright 2026 Sony Corporation
 */

import type { Device, Optics } from '@ips-device-verse/twin-service/schema';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useAuth } from '../contexts/auth-context';
import { resolveOptics, type ResolvedOptics } from '../scene/camera-optics';
import { createThrottle } from '../scene/throttle';

export interface UseCameraOpticsOptions {
  worldId: string;
  devices: readonly Device[];
  /**
   * The row the service wrote, so the caller can show it before anything else
   * says so. Keep it stable: a new identity rebuilds the throttle and drops a
   * write waiting in it.
   */
  onSaved: (device: Device) => void;
}

export interface UseCameraOpticsResult {
  /** Every device's optics, an edit in flight included. */
  optics: Record<string, ResolvedOptics>;
  /** Merges a change into the device's optics and pulls the rest back inside the lens. */
  set: (deviceId: string, change: Partial<Optics>) => void;
  pending: Record<string, boolean>;
  errors: Record<string, string>;
}

/** A focal-length drag fires continuously; one write per fifth of a second is plenty. */
const WRITE_INTERVAL_MS = 200;

const omit = <T>(source: Record<string, T>, key: string): Record<string, T> => {
  const { [key]: _removed, ...rest } = source;
  return rest;
};

/**
 * Lens and sensor settings, which are planning data on the device row
 * (camera-simulation.md §4) and so need no gateway to take effect.
 *
 * An edit is held until the write that carried it returns; the returned row is
 * handed to `onSaved`, so the value never snaps back to the old one in between.
 */
export const useCameraOptics = ({ worldId, devices, onSaved }: UseCameraOpticsOptions): UseCameraOpticsResult => {
  const { api } = useAuth();
  const [edits, setEdits] = useState<Record<string, Optics>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  const throttle = useMemo(
    () =>
      createThrottle<Optics>({
        intervalMs: WRITE_INTERVAL_MS,
        send: (deviceId, optics) => {
          api.updateDevice(worldId, deviceId, { optics }).then(
            (device) => {
              onSaved(device);
              // A later edit is still on its way; only this one is done.
              setEdits((previous) => (previous[deviceId] === optics ? omit(previous, deviceId) : previous));
            },
            (error: Error) => {
              setEdits((previous) => (previous[deviceId] === optics ? omit(previous, deviceId) : previous));
              setErrors((previous) => ({ ...previous, [deviceId]: error.message }));
            }
          );
        },
      }),
    [api, worldId, onSaved]
  );
  useEffect(() => () => throttle.cancel(), [throttle]);

  const optics = useMemo(
    () =>
      Object.fromEntries(
        devices.map((device) => [
          device.device_id,
          resolveOptics(device.model, edits[device.device_id] ?? device.optics),
        ])
      ),
    [devices, edits]
  );

  const set = useCallback(
    (deviceId: string, change: Partial<Optics>) => {
      const current = optics[deviceId];
      if (!current) {
        return;
      }
      const next = resolveOptics(current.body.model, { ...current.optics, ...change }).optics;
      setEdits((previous) => ({ ...previous, [deviceId]: next }));
      setErrors((previous) => omit(previous, deviceId));
      throttle.push(deviceId, next);
    },
    [optics, throttle]
  );

  const pending = useMemo(() => Object.fromEntries(Object.keys(edits).map((id) => [id, true])), [edits]);

  return { optics, set, pending, errors };
};
